import { cn } from "cn";
import { useState } from "react";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine } from "@/components/ui/icons";

import {
  ActiveFilterChip,
  EMPTY_VALUE_LABEL,
} from "../_components/active-filter-chip";
import { useFilters } from "../_hooks/use-filters";
import { normalizeBoolean } from "../codecs";
import type { FilterField } from "../types";

/** Two-state toggle. Writes canonical "true"/"false". Shows the shared "is"
 * operator chip like every other select widget, so the chip family stays
 * consistent across callers. Labels default to "Yes"/"No" but a field can
 * override via `trueLabel`/`falseLabel` (e.g. checkout-links status →
 * "Active"/"Inactive"). */
export const BooleanFilter = ({ field }: { field: FilterField }) => {
  const { filters, modify, clear } = useFilters();
  const value = normalizeBoolean(
    (filters as Record<string, unknown>)[field.key],
  );
  const [open, setOpen] = useState(() => value === undefined);

  const trueLabel = field.trueLabel ?? "Yes";
  const falseLabel = field.falseLabel ?? "No";
  const booleanOptions = [
    { label: trueLabel, value: true },
    { label: falseLabel, value: false },
  ] as const;

  const valueLabel =
    value === true
      ? trueLabel
      : value === false
        ? falseLabel
        : EMPTY_VALUE_LABEL;

  return (
    <ActiveFilterChip
      field={field}
      valueLabel={valueLabel}
      open={open}
      onOpenChange={setOpen}
      onRemove={() => clear(field.key)}
    >
      <Command
        className={cn(
          /* sorted */
          "bg-bg-surface-elevate-low",
          field.popoverWidthClassName ?? "w-50",
        )}
        shouldFilter={false}
      >
        <CommandList>
          <CommandGroup>
            {booleanOptions.map((option) => (
              <CommandItem
                key={String(option.value)}
                onSelect={() => {
                  modify({ [field.key]: option.value ? "true" : "false" });
                  setOpen(false);
                }}
                className={cn(
                  /* sorted */
                  "flex",
                  "items-center justify-between",
                  "cursor-pointer",
                )}
              >
                <span>{option.label}</span>
                {value === option.value && (
                  <BwCheckmarkLine className="size-4 shrink-0" />
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </ActiveFilterChip>
  );
};
